import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  Star,
  CheckCircle,
  CreditCard,
  CalendarDays,
  Clock as ClockIcon,
  Video,
  Phone,
  MessageSquare,
  AlertCircle,
  Info,
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertTriangle
} from 'lucide-react';
import { useApp } from '../context/useApp.js';
import { appointmentService, lawyerSearchService, chatService } from '../services/firebaseService.js';
import toast from 'react-hot-toast';

const BookingCreate = () => {
  const { user, language, checkFeatureAccess, recordFeatureUsage } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const lawyerId = location.state?.lawyerId;

  // State for form data
  const [bookingData, setBookingData] = useState({
    consultationType: 'general',
    date: '',
    time: '',
    duration: 60,
    subject: '',
    description: '',
    isEmergency: false
  });

  // State for UI
  const [lawyer, setLawyer] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showTimeSlots, setShowTimeSlots] = useState(false);

  const translations = {
    english: {
      title: 'Book Consultation',
      subtitle: 'Schedule your legal consultation',
      backToSearch: 'Back to Search',
      consultationType: 'Consultation Type',
      general: 'General Consultation',
      emergency: 'Emergency Consultation',
      date: 'Date',
      time: 'Time',
      duration: 'Duration',
      subject: 'Subject',
      description: 'Description',
      descriptionPlaceholder: 'Brief description of your legal issue...',
      subjectPlaceholder: 'Brief subject of your consultation...',
      availableSlots: 'Available Slots',
      noSlotsAvailable: 'No slots available for selected date',
      selectDate: 'Select a date to see available slots',
      totalAmount: 'Total Amount',
      consultationFee: 'Consultation Fee',
      emergencyFee: 'Emergency Fee',
      platformFee: 'Platform Fee',
      taxes: 'Taxes',
      confirmBooking: 'Confirm Booking',
      bookingSuccess: 'Booking created successfully!',
      redirectingToPayment: 'Redirecting to payment...',
      error: 'Error creating booking',
      retry: 'Retry',
      loading: 'Loading lawyer details...',
      loadingSlots: 'Loading available slots...',
      minutes: 'minutes',
      hours: 'hours',
      emergencyNote: 'Emergency consultations are charged at 2x the regular rate',
      selectDateFirst: 'Please select a date first',
      noLawyerFound: 'Lawyer not found',
      lawyerNotFound: 'The requested lawyer could not be found',
      backToLawyerSearch: 'Back to Lawyer Search'
    },
    hindi: {
      title: 'परामर्श बुक करें',
      subtitle: 'अपना कानूनी परामर्श शेड्यूल करें',
      backToSearch: 'खोज पर वापस जाएं',
      consultationType: 'परामर्श का प्रकार',
      general: 'सामान्य परामर्श',
      emergency: 'आपातकालीन परामर्श',
      date: 'तारीख',
      time: 'समय',
      duration: 'अवधि',
      subject: 'विषय',
      description: 'विवरण',
      descriptionPlaceholder: 'अपने कानूनी मुद्दे का संक्षिप्त विवरण...',
      subjectPlaceholder: 'अपने परामर्श का संक्षिप्त विषय...',
      availableSlots: 'उपलब्ध स्लॉट',
      noSlotsAvailable: 'चयनित तारीख के लिए कोई स्लॉट उपलब्ध नहीं है',
      selectDate: 'उपलब्ध स्लॉट देखने के लिए तारीख चुनें',
      totalAmount: 'कुल राशि',
      consultationFee: 'परामर्श शुल्क',
      emergencyFee: 'आपातकालीन शुल्क',
      platformFee: 'प्लेटफॉर्म शुल्क',
      taxes: 'कर',
      confirmBooking: 'बुकिंग की पुष्टि करें',
      bookingSuccess: 'बुकिंग सफलतापूर्वक बनाई गई!',
      redirectingToPayment: 'भुगतान पर पुनर्निर्देशित कर रहा है...',
      error: 'बुकिंग बनाने में त्रुटि',
      retry: 'पुनः प्रयास करें',
      loading: 'वकील का विवरण लोड हो रहा है...',
      loadingSlots: 'उपलब्ध स्लॉट लोड हो रहे हैं...',
      minutes: 'मिनट',
      hours: 'घंटे',
      emergencyNote: 'आपातकालीन परामर्श नियमित दर से 2x पर चार्ज किए जाते हैं',
      selectDateFirst: 'कृपया पहले तारीख चुनें',
      noLawyerFound: 'वकील नहीं मिला',
      lawyerNotFound: 'अनुरोधित वकील नहीं मिल सका',
      backToLawyerSearch: 'वकील खोज पर वापस जाएं'
    }
  };

  const t = translations[language] || translations.english;

  const consultationTypes = [
    { value: 'general', label: t.general, icon: MessageSquare },
    { value: 'emergency', label: t.emergency, icon: AlertCircle }
  ];

  const durationOptions = [
    { value: 30, label: '30 minutes' },
    { value: 60, label: '1 hour' },
    { value: 90, label: '1.5 hours' },
    { value: 120, label: '2 hours' }
  ];

  // Load lawyer details
  const loadLawyerDetails = async () => {
    if (!lawyerId) {
      setError(t.noLawyerFound);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const result = await lawyerSearchService.getLawyerDetails(lawyerId);
      
      if (result.success) {
        setLawyer(result.data);
      } else {
        setError(result.error);
        toast.error(result.error);
      }
    } catch (error) {
      console.error('Error loading lawyer details:', error);
      setError('Failed to load lawyer details');
      toast.error('Failed to load lawyer details');
    } finally {
      setLoading(false);
    }
  };

  // Load available slots for selected date
  const loadAvailableSlots = async (date) => {
    if (!date || !lawyerId) return;

    try {
      setLoading(true);
      const result = await lawyerSearchService.getLawyerAvailability(lawyerId, date);
      
      if (result.success) {
        // Generate time slots based on availability
        const slots = generateTimeSlots(result.data, date);
        setAvailableSlots(slots);
      } else {
        // If no availability data, generate default slots
        const slots = generateDefaultTimeSlots(date);
        setAvailableSlots(slots);
      }
    } catch (error) {
      console.error('Error loading available slots:', error);
      // Generate default slots on error
      const slots = generateDefaultTimeSlots(date);
      setAvailableSlots(slots);
    } finally {
      setLoading(false);
    }
  };

  // Generate time slots based on availability data
  const generateTimeSlots = (availability, date) => {
    if (!availability) return generateDefaultTimeSlots(date);

    const slots = [];
    const startTime = availability.start_time || '09:00';
    const endTime = availability.end_time || '17:00';
    const maxAppointments = availability.max_appointments || 10;
    const currentAppointments = availability.current_appointments || 0;

    if (currentAppointments >= maxAppointments) return [];

    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);

    while (start < end) {
      const timeString = start.toTimeString().slice(0, 5);
      slots.push({
        time: timeString,
        available: true,
        formatted: start.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true 
        })
      });
      start.setMinutes(start.getMinutes() + 60);
    }

    return slots;
  };

  // Generate default time slots
  const generateDefaultTimeSlots = (date) => {
    const slots = [];
    const startHour = 9;
    const endHour = 17;

    for (let hour = startHour; hour < endHour; hour++) {
      const timeString = `${hour.toString().padStart(2, '0')}:00`;
      slots.push({
        time: timeString,
        available: true,
        formatted: new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true 
        })
      });
    }

    return slots;
  };

  // Calculate total amount
  const calculateTotalAmount = () => {
    if (!lawyer) return 0;

    let baseAmount = lawyer.consultation_fee || 0;
    
    if (bookingData.isEmergency) {
      baseAmount *= 2; // Emergency fee is 2x
    }

    const platformFee = baseAmount * 0.05; // 5% platform fee
    const taxes = (baseAmount + platformFee) * 0.18; // 18% GST

    return {
      consultationFee: baseAmount,
      platformFee,
      taxes,
      total: baseAmount + platformFee + taxes
    };
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setBookingData(prev => ({ ...prev, [field]: value }));
    
    // Load available slots when date changes
    if (field === 'date') {
      loadAvailableSlots(value);
      setShowTimeSlots(true);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill all required fields');
      return;
    }

    // Check if user can book consultations
    const canBook = await checkFeatureAccess('consultation');
    if (!canBook) {
      toast.error('You have reached your consultation limit. Please upgrade your plan.');
      navigate('/plans');
      return;
    }

    try {
      setSubmitting(true);

      const appointmentData = {
        lawyer_id: lawyerId,
        client_id: user?.id,
        appointment_date: bookingData.date,
        start_time: bookingData.time,
        end_time: calculateEndTime(bookingData.time, bookingData.duration),
        consultation_type: bookingData.consultationType,
        subject: bookingData.subject,
        description: bookingData.description,
        amount: calculateTotalAmount().total,
        is_emergency: bookingData.consultationType === 'emergency',
        duration_minutes: bookingData.duration,
        status: 'pending',
        payment_status: 'pending',
        created_at: new Date().toISOString()
      };

      const result = await appointmentService.createAppointment(appointmentData);
      
      if (result.success) {
        // Record feature usage
        const usageResult = await recordFeatureUsage('consultation');
        if (!usageResult) {
          console.warn('Failed to record consultation usage');
        }
        
        // Create chat room for this booking
        try {
          const chatResult = await chatService.createOrGetChatRoom(lawyerId, user?.id);
          if (chatResult.success) {
            console.log('Chat room created:', chatResult.data);
          }
        } catch (chatError) {
          console.error('Error creating chat room:', chatError);
        }
        
        toast.success(t.bookingSuccess);
        toast.success(t.redirectingToPayment);
        
        // Navigate to payment with booking data
        navigate('/payment/process', { 
          state: { 
            booking: result.data,
            amount: calculateTotalAmount()
          } 
        });
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error(t.error);
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate end time based on start time and duration
  const calculateEndTime = (startTime, durationMinutes) => {
    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(start.getTime() + durationMinutes * 60000);
    return end.toTimeString().slice(0, 5);
  };

  // Validate form
  const validateForm = () => {
    return (
      bookingData.date &&
      bookingData.time &&
      bookingData.subject.trim() &&
      bookingData.description.trim()
    );
  };

  // Load initial data
  useEffect(() => {
    loadLawyerDetails();
  }, [lawyerId]);

  // Calculate amounts
  const amounts = calculateTotalAmount();

  // Loading state
  if (loading && !lawyer) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">{t.loading}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !lawyer) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t.noLawyerFound}</h3>
          <p className="text-gray-600 mb-4">{t.lawyerNotFound}</p>
          <button
            onClick={() => navigate('/lawyer-search')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t.backToLawyerSearch}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/lawyer-search')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t.backToSearch}
          </button>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t.title}</h1>
          <p className="text-gray-600">{t.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <form onSubmit={handleSubmit}>
                {/* Lawyer Info */}
                {lawyer && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                        {lawyer.user_profiles?.full_name?.charAt(0) || 'L'}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {lawyer.user_profiles?.full_name || 'Lawyer Name'}
                        </h3>
                        <p className="text-gray-600">
                          {lawyer.specialization?.join(', ') || 'General Law'}
                        </p>
                        <div className="flex items-center space-x-4 mt-2">
                          <div className="flex items-center">
                            <Star className="w-4 h-4 text-yellow-400 mr-1" />
                            <span className="text-sm text-gray-600">
                              {lawyer.average_rating ? lawyer.average_rating.toFixed(1) : 'N/A'}
                            </span>
                          </div>
                          <div className="flex items-center">
                            <MapPin className="w-4 h-4 text-gray-400 mr-1" />
                            <span className="text-sm text-gray-600">
                              {lawyer.city || 'Location not specified'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Consultation Type */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {t.consultationType}
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {consultationTypes.map((type) => (
                      <label
                        key={type.value}
                        className={`relative flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                          bookingData.consultationType === type.value
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <input
                          type="radio"
                          name="consultationType"
                          value={type.value}
                          checked={bookingData.consultationType === type.value}
                          onChange={(e) => handleInputChange('consultationType', e.target.value)}
                          className="sr-only"
                        />
                        <type.icon className="w-5 h-5 text-blue-600 mr-3" />
                        <div>
                          <div className="font-medium text-gray-900">{type.label}</div>
                          {type.value === 'emergency' && (
                            <div className="text-sm text-gray-500 mt-1">
                              {t.emergencyNote}
                            </div>
                          )}
                        </div>
                        {bookingData.consultationType === type.value && (
                          <CheckCircle className="w-5 h-5 text-blue-600 absolute top-4 right-4" />
                        )}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Date and Time */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.date}
                    </label>
                    <input
                      type="date"
                      value={bookingData.date}
                      onChange={(e) => handleInputChange('date', e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  {/* Duration */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.duration}
                    </label>
                    <select
                      value={bookingData.duration}
                      onChange={(e) => handleInputChange('duration', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {durationOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Time Slots */}
                {showTimeSlots && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      {t.time}
                    </label>
                    {loading ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 text-blue-600 animate-spin mr-2" />
                        <span className="text-gray-600">{t.loadingSlots}</span>
                      </div>
                    ) : availableSlots.length > 0 ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {availableSlots.map((slot) => (
                          <button
                            key={slot.time}
                            type="button"
                            onClick={() => handleInputChange('time', slot.time)}
                            className={`p-3 text-center rounded-lg border transition-colors ${
                              bookingData.time === slot.time
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : 'border-gray-300 hover:border-gray-400 text-gray-700'
                            }`}
                          >
                            {slot.formatted}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-500">
                        {t.noSlotsAvailable}
                      </div>
                    )}
                  </div>
                )}

                {/* Subject */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.subject}
                  </label>
                  <input
                    type="text"
                    value={bookingData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    placeholder={t.subjectPlaceholder}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Description */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.description}
                  </label>
                  <textarea
                    value={bookingData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder={t.descriptionPlaceholder}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={submitting || !validateForm()}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {submitting ? (
                    <div className="flex items-center justify-center">
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Creating Booking...
                    </div>
                  ) : (
                    t.confirmBooking
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Price Breakdown */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t.totalAmount}</h3>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t.consultationFee}</span>
                  <span className="font-medium">₹{amounts.consultationFee.toFixed(2)}</span>
                </div>
                
                {bookingData.isEmergency && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t.emergencyFee}</span>
                    <span className="font-medium">₹{amounts.consultationFee.toFixed(2)}</span>
                  </div>
                )}
                
                <div className="flex justify-between">
                  <span className="text-gray-600">{t.platformFee}</span>
                  <span className="font-medium">₹{amounts.platformFee.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">{t.taxes}</span>
                  <span className="font-medium">₹{amounts.taxes.toFixed(2)}</span>
                </div>
                
                <div className="border-t pt-3">
                  <div className="flex justify-between">
                    <span className="text-lg font-semibold text-gray-900">{t.totalAmount}</span>
                    <span className="text-lg font-semibold text-gray-900">₹{amounts.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-start">
                  <Info className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Booking Information</p>
                    <ul className="space-y-1 text-xs">
                      <li>• Consultation will be conducted via video call</li>
                      <li>• You'll receive a link 15 minutes before</li>
                      <li>• Cancellation allowed up to 24 hours before</li>
                      <li>• Payment will be processed after confirmation</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingCreate;
